import Form from "@rjsf/react-bootstrap";
import React, { FunctionComponent, useEffect } from "react";
import { JSONSchema7 } from "json-schema";
import { Link, useNavigate } from "react-router";
import { toast } from "react-toastify";
import firebase from "firebase/compat/app";
import validator from "@rjsf/validator-ajv8";
import { UiSchema } from "@rjsf/utils";
import { Helmet } from "react-helmet";

import { useAuthRegistration } from "../../hooks/useAuthRegistration";
import { getErrorByCode } from "../../helper/firebase/getErrorByCode";
import { AuthLayout } from "./AuthLayout";

const schema: JSONSchema7 = {
  type: "object",
  required: ["email", "password", "passwordConfirmation"],
  properties: {
    email: {
      type: "string",
      title: "E-Mail"
    },
    password: {
      type: "string",
      title: "Passwort",
      minLength: 6
    },
    passwordConfirmation: {
      type: "string",
      title: "Passwort bestätigen",
      minLength: 6
    }
  }
};

const uiSchema: UiSchema = {
  email: {
    "ui:widget": "email"
  },
  password: {
    "ui:widget": "password"
  },
  passwordConfirmation: {
    "ui:widget": "password"
  }
};

function transformErrors(errors: any): any {
  return errors.map((error: any): any => {
    if (error.name === "minLength") {
      error.message = "Das Passwort sollte nicht kürzer als 6 Zeichen sein.";
    }
    return error;
  });
}

interface Props {
  user: firebase.User | null | undefined;
}

const SignUp: FunctionComponent<Props> = ({ user }: Props) => {
  const navigate = useNavigate();
  const { createUserWithEmailAndPassword, isLoading } = useAuthRegistration();

  useEffect(() => {
    if (user) {
      navigate("/");
    }
  }, [user, navigate]);

  const submitSignUp = async (formData: any): Promise<void> => {
    const { email, password, passwordConfirmation } = formData;

    if (!password === passwordConfirmation) {
      toast.error("Passwörter stimmen nicht überein.");
    }

    createUserWithEmailAndPassword(email, password).catch((e) => {
      toast.error(`Die Registrierung ist fehlgeschlagen. ${getErrorByCode(e.code)}`);
    });
  };

  return (
    <>
      <Helmet>
        <title>Spielgruppe Online - Registrieren</title>
        <meta name="description" content="Erstellen Sie ein Konto, um Ihre Spielgruppe-Anmeldungen zu verwalten" />
      </Helmet>
      <AuthLayout
        title={"Konto erstellen"}
        lead={"Registrieren Sie sich, um Anmeldungen zu verwalten"}
        footer={
          <p>
            Bereits ein Konto? <Link to="/">Zur Anmeldung</Link>
          </p>
        }>
        <Form
          validator={validator}
          schema={schema}
          uiSchema={uiSchema}
          onSubmit={(e) => submitSignUp(e.formData)}
          showErrorList={false}
          transformErrors={transformErrors}
          className="signup-form">
          <p className="auth-card__hint">
            Mit der Registrierung akzeptieren Sie die{" "}
            <a target={"_blank"} rel={"noreferrer"} href="/agb">
              AGB
            </a>
          </p>
          <button disabled={isLoading} type="submit" className="btn btn-primary btn-lg">
            Registrieren
          </button>
        </Form>
      </AuthLayout>
    </>
  );
};

export default SignUp;
